import { formatRelativeTime } from '@/helpers/format-relative-time';
import { cn } from '@/helpers/utils';
import type { NotificationItemDto } from '@/types/notifications';
import { getNotificationTypeConfig } from './notification-type-config';

export type NotificationItemProps = {
  notification: NotificationItemDto;
  onActivate: (n: NotificationItemDto) => void;
};

export function NotificationItem({ notification, onActivate }: NotificationItemProps) {
  const cfg = getNotificationTypeConfig(notification.type);
  const Icon = cfg.icon;
  const iconColorClass = notification.icon_color || cfg.iconColorClass;

  return (
    <article
      className={cn(
        'transition-colors',
        !notification.is_read
          ? 'border-l-[3px] border-l-[hsl(var(--pitch-green))] bg-[hsl(var(--pitch-green)/0.1)]'
          : 'bg-transparent',
      )}
    >
      <button
        type="button"
        aria-label={
          notification.is_read
            ? `${notification.title}. ${notification.body}`
            : `Unread: ${notification.title}. ${notification.body}`
        }
        className={cn(
          'flex w-full cursor-pointer gap-3 rounded-md px-3 py-1.5 text-left transition-colors',
          'hover:bg-muted/50',
        )}
        onClick={() => onActivate(notification)}
      >
        <div className="flex w-2 shrink-0 flex-col items-center pt-2" aria-hidden>
          {!notification.is_read ? (
            <span className="h-2 w-2 rounded-full bg-[hsl(var(--pitch-green))] ring-2 ring-[hsl(var(--pitch-green)/0.1)]" />
          ) : null}
        </div>
        <div className="flex-shrink-0 pt-0.5">
          <Icon className={cn('h-5 w-5', iconColorClass)} aria-hidden />
        </div>
        <div className="min-w-0 flex-1 space-y-0.5">
          <p className="text-sm font-medium leading-snug text-foreground">{notification.title}</p>
          <p className="text-xs leading-snug text-muted-foreground">{notification.body}</p>
        </div>
        <div className="flex-shrink-0 pt-0.5 text-[10px] text-muted-foreground tabular-nums">
          {formatRelativeTime(notification.created_at)}
        </div>
      </button>
    </article>
  );
}
